"use client";

import { useState, useEffect } from "react";
import { MapPin, Loader2, Navigation, Store, Sparkles } from "lucide-react";

// Friendly progress messages for each stage
const PROGRESS_MESSAGES = [
  { message: "Looking up your location...", icon: MapPin },
  { message: "Finding garden centers near you...", icon: Store },
  { message: "Calculating distances...", icon: Sparkles },
  { message: "Almost there...", icon: Sparkles },
];

interface ZipcodeEntryProps {
  onZipcodeSubmit: (zipcode: string) => Promise<void>;
  loading?: boolean;
  primaryColor?: string;
  initialZipcode?: string;
  isRetailerFlow?: boolean;
}

export function ZipcodeEntry({
  onZipcodeSubmit,
  loading = false,
  primaryColor: _primaryColor = "#10b981", // emerald-500 default
  initialZipcode,
  isRetailerFlow = false,
}: ZipcodeEntryProps) {
  const [zipcode, setZipcode] = useState(initialZipcode || "");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);

  // Reset success animation after it plays
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Cycle through progress messages when submitting
  useEffect(() => {
    if (isSubmitting || isDetecting) {
      setProgressIndex(0); // Reset to first message
      const interval = setInterval(() => {
        setProgressIndex((prev) => {
          // Cycle through messages, staying on the last one
          if (prev < PROGRESS_MESSAGES.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 1500); // Change message every 1.5 seconds
      return () => clearInterval(interval);
    } else {
      setProgressIndex(0);
    }
  }, [isSubmitting, isDetecting]);

  const validateZipcode = (value: string): boolean => {
    // US zipcode: 5 digits or 5+4 format
    const zipcodeRegex = /^\d{5}(-\d{4})?$/;
    return zipcodeRegex.test(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 5);
    setZipcode(value);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!zipcode) {
      setError("Please enter a ZIP code");
      return;
    }

    if (!validateZipcode(zipcode)) {
      setError("Please enter a valid 5-digit ZIP code");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onZipcodeSubmit(zipcode);
      setShowSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to verify ZIP code"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAutoDetect = async () => {
    setIsDetecting(true);
    setError(null);

    try {
      // Step 1: Get coordinates from browser geolocation
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error("Geolocation is not supported by your browser"));
            return;
          }
          navigator.geolocation.getCurrentPosition(
            resolve,
            (err) => {
              if (err.code === err.PERMISSION_DENIED) {
                reject(
                  new Error(
                    "Location access denied. Please enter your ZIP code manually."
                  )
                );
              } else if (err.code === err.TIMEOUT) {
                reject(
                  new Error(
                    "Location request timed out. Please enter your ZIP code manually."
                  )
                );
              } else {
                reject(new Error("Unable to determine your location"));
              }
            },
            { timeout: 10000, enableHighAccuracy: false }
          );
        }
      );

      const { latitude, longitude } = position.coords;

      // Step 2: Reverse-geocode coordinates to zipcode using BigDataCloud (free, no API key)
      const geoResponse = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );

      if (!geoResponse.ok) {
        throw new Error("Could not determine ZIP code from your location");
      }

      const geoData = await geoResponse.json();
      const detectedZipcode = geoData.postcode;

      if (!detectedZipcode || !/^\d{5}/.test(detectedZipcode)) {
        throw new Error(
          "Could not determine a US ZIP code from your location. Please enter it manually."
        );
      }

      const zip = detectedZipcode.slice(0, 5);

      // Step 3: Submit the detected zipcode
      setZipcode(zip);
      setShowSuccess(true);
      await onZipcodeSubmit(zip);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to detect location"
      );
    } finally {
      setIsDetecting(false);
    }
  };

  const isLoading = loading || isSubmitting || isDetecting;

  return (
    <div className="w-full max-w-md mx-auto px-4 md:px-0">
      <div className="bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-xl shadow-lg p-6 md:p-8 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-plantista-100 dark:bg-plantista-800 border border-plantista-200 dark:border-plantista-700 mb-2 md:mb-4">
            <MapPin className="w-6 h-6 md:w-8 md:h-8 text-plantista-600 dark:text-plantista-400" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white font-display">
            {isRetailerFlow
              ? "Do we design in your area?"
              : "Where are you located?"}
          </h2>
          <p className="text-sm md:text-base text-plantista-600 dark:text-plantista-300">
            {isRetailerFlow
              ? "Enter your ZIP code to check if we support designs in your area"
              : "Enter your ZIP code to find garden centers near you"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Zipcode Input */}
          <div className="relative">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={5}
              value={zipcode}
              onChange={handleInputChange}
              placeholder="12345"
              disabled={isLoading}
              className={`
                w-full px-6 py-4 text-center text-2xl md:text-3xl font-semibold
                border-2 rounded-lg transition-all
                focus:outline-none focus:ring-4 bg-gray-50 dark:bg-plantista-950 text-plantista-950 dark:text-plantista-50
                ${
                  error
                    ? "border-red-500/50 focus:border-red-500 focus:ring-red-900/50"
                    : "border-gray-200 dark:border-plantista-700 focus:border-plantista-500 focus:ring-plantista-500/50 dark:focus:ring-plantista-900/50"
                }
                ${isLoading ? "bg-gray-100 dark:bg-plantista-900 cursor-not-allowed text-gray-400 dark:text-plantista-600" : ""}
                ${showSuccess ? "border-plantista-500 dark:border-plantista-400 bg-white dark:bg-plantista-900" : ""}
              `}
              aria-label="ZIP code"
              aria-describedby={error ? "zipcode-error" : undefined}
              aria-invalid={error ? "true" : "false"}
            />
            {showSuccess && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <div className="animate-scale-in">
                  <svg
                    className="w-8 h-8 text-plantista-500 dark:text-plantista-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div
              id="zipcode-error"
              className="flex items-start space-x-2 text-sm text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
              role="alert"
            >
              <svg
                className="w-5 h-5 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !zipcode}
            className={`
              w-full px-6 py-4 rounded-lg font-semibold text-plantista-950 dark:text-plantista-950
              transition-all duration-200
              focus:outline-none focus:ring-4 focus:ring-plantista-500/50 dark:focus:ring-plantista-900
              ${
                isLoading || !zipcode
                  ? "bg-gray-300 dark:bg-plantista-800 text-gray-500 dark:text-plantista-500 cursor-not-allowed"
                  : "bg-plantista-400 dark:bg-plantista-400 hover:bg-plantista-500 dark:hover:bg-plantista-300 active:translate-y-[2px] active:translate-x-[2px] shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:shadow-[2px_2px_0px_0px]"
              }
            `}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="animate-fade-in">
                  {PROGRESS_MESSAGES[progressIndex].message}
                </span>
              </span>
            ) : isRetailerFlow ? (
              "Check My Area"
            ) : (
              "Find Nearby Garden Centers"
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-plantista-800"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white dark:bg-plantista-900 text-gray-500 dark:text-plantista-500">
              or
            </span>
          </div>
        </div>

        {/* Auto-detect Button */}
        <button
          type="button"
          onClick={handleAutoDetect}
          disabled={isLoading}
          className={`
            w-full px-6 py-3 rounded-lg font-medium
            border-2 transition-all
            focus:outline-none focus:ring-4 focus:ring-plantista-500/50 dark:focus:ring-plantista-900
            ${
              isLoading
                ? "border-gray-200 dark:border-plantista-800 bg-gray-50 dark:bg-plantista-900 text-gray-400 dark:text-plantista-600 cursor-not-allowed"
                : "border-plantista-200 dark:border-plantista-700 bg-white dark:bg-plantista-900 text-plantista-700 dark:text-plantista-300 hover:border-plantista-500 dark:hover:border-plantista-500 hover:bg-plantista-50 dark:hover:bg-plantista-800 hover:text-plantista-900 dark:hover:text-white"
            }
          `}
        >
          {isDetecting ? (
            <span className="flex items-center justify-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="animate-fade-in">
                {PROGRESS_MESSAGES[progressIndex].message}
              </span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-2">
              <Navigation className="w-5 h-5" />
              <span>Auto-detect my location</span>
            </span>
          )}
        </button>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-plantista-50/50 dark:bg-plantista-900/50 border border-plantista-200 dark:border-plantista-800 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Store className="w-5 h-5 text-plantista-500 dark:text-plantista-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-plantista-700 dark:text-plantista-300">
            <p className="font-medium mb-1 text-plantista-900 dark:text-plantista-200">
              Why enter your ZIP code?
            </p>
            <p>
              {isRetailerFlow
                ? "We use your ZIP code to determine your USDA hardiness zone and verify that we can create designs suited to your climate."
                : "We'll connect you with local garden centers that carry the plants we recommend, so you can easily find everything you need for your garden design."}
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
